import React from "react";
import { useLang } from "@/context/LanguageContext";
import { DogIcon, CatIcon, HouseIcon, PawIcon } from "@/components/Icons";
import Reveal from "@/components/Reveal";

const PriceRow = ({ label, value, accent }) => (
  <div className="flex items-center justify-between gap-3 py-2.5 border-b border-dashed border-[#f7c5c0] last:border-b-0">
    <span className="text-sm text-[#6b3f2a]/85">{label}</span>
    <span className={`font-display text-base sm:text-lg ${accent ? "text-[#f4956a]" : "text-[#6b3f2a]"}`}>
      {value}
    </span>
  </div>
);

const ServiceCard = ({ icon, accent, title, text, prices, tax, testid }) => (
  <Reveal>
    <article className="cute-card h-full flex flex-col text-center items-center" data-testid={testid}>
      <div
        className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5 mx-auto"
        style={{ background: accent }}
      >
        <div className="text-white">{icon}</div>
      </div>
      <h3 className="font-display text-2xl mb-3">{title}</h3>
      <p className="text-[#6b3f2a]/80 leading-relaxed mb-5 text-sm sm:text-base">{text}</p>
      <div className="bg-[#fdf3ec] rounded-2xl p-4 mt-auto w-full text-left">
        {prices.map((p, i) => (
          <PriceRow key={i} label={p.label} value={p.value} accent={i === 0} />
        ))}
      </div>
      <p className="text-xs text-[#6b3f2a]/60 italic mt-3 text-center">{tax}</p>
    </article>
  </Reveal>
);

const Services = () => {
  const { t } = useLang();

  const cards = [
    {
      testid: "service-card-walk",
      icon: <DogIcon size={32} />,
      accent: "linear-gradient(135deg, #f4956a, #e07a4d)",
      title: t.services.walk.title,
      text: t.services.walk.text,
      prices: [
        { label: t.services.walk.p1, value: t.services.walk.p1v },
        { label: t.services.walk.p2, value: t.services.walk.p2v },
        { label: t.services.walk.p3, value: t.services.walk.p3v },
      ],
    },
    {
      testid: "service-card-day",
      icon: <HouseIcon size={30} />,
      accent: "linear-gradient(135deg, #a8d5ba, #76b990)",
      title: t.services.day.title,
      text: t.services.day.text,
      prices: [
        { label: t.services.day.p1, value: t.services.day.p1v },
        { label: t.services.day.p2, value: t.services.day.p2v },
      ],
    },
    {
      testid: "service-card-cat",
      icon: <CatIcon size={30} />,
      accent: "linear-gradient(135deg, #f7c5c0, #e89d97)",
      title: t.services.cat.title,
      text: t.services.cat.text,
      prices: [
        { label: t.services.cat.p1, value: t.services.cat.p1v },
        { label: t.services.cat.p2, value: t.services.cat.p2v },
        { label: t.services.cat.p3, value: t.services.cat.p3v },
      ],
    },
  ];

  return (
    <section id="services" className="section-soft py-20 sm:py-28 relative" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="text-center max-w-2xl mx-auto mb-14">
          <span className="font-script text-3xl text-[#f4956a] flex items-center justify-center gap-2">
            <PawIcon size={22} /> Angebot
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2">{t.services.title}</h2>
          <p className="text-[#6b3f2a]/75 mt-4 text-sm sm:text-base">{t.services.subtitle}</p>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {cards.map((c) => (
            <ServiceCard key={c.testid} {...c} tax={t.services.tax} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
